import { Link } from 'react-router-dom'

import { Badge, type BadgeTone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import type { CourtItem } from '@/lib/venue/courtTypes'
import { toCourtCardViewModel } from '@/lib/venue/courtViewModel'

/** 🚨 배지 색만 프런트가 매핑한다 — 텍스트는 서버 라벨(`statusLabel`) 그대로(`Badge.tsx` 규칙 승계). */
const STATUS_TONE: Record<CourtItem['status'], BadgeTone> = {
  OPEN: 'success',
  SUSPENDED: 'neutral',
  MAINTENANCE: 'warning',
}

export function CourtCard({
  venueId,
  court,
  editable,
  onEdit,
  onDelete,
}: {
  venueId: string
  court: CourtItem
  /** venue 단위 게이팅(#597 §1 `editable`) — '수정' 버튼을 잠근다. */
  editable: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const vm = toCourtCardViewModel(court)
  const deleteLocked = !court.deletable

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-ink-700">{court.name}</p>
            <p className="text-xs text-slateish-500">
              {court.floorTypeLabel ?? '바닥 재질 미입력'}
              {court.courtTypeLabel ? ` · ${court.courtTypeLabel}` : ''}
              {court.sizeNote ? ` · ${court.sizeNote}` : ''}
            </p>
          </div>
          <Badge tone={STATUS_TONE[court.status]}>{court.statusLabel}</Badge>
        </div>

        <dl className="grid grid-cols-2 gap-2 text-xs text-slateish-600">
          <div>
            <dt className="text-slateish-400">예약 단위</dt>
            <dd>{court.reservationUnitLabel}</dd>
          </div>
          <div>
            <dt className="text-slateish-400">최대 예약 시간</dt>
            <dd>{court.maxReservationLabel}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-slateish-400">기본 요금</dt>
            <dd>{vm.feeText}</dd>
            {vm.priceBandSummaryText ? (
              <dd className="mt-0.5 text-slateish-500">{vm.priceBandSummaryText}</dd>
            ) : null}
          </div>
          <div className="col-span-2">
            <dt className="text-slateish-400">운영시간</dt>
            <dd>{vm.operatingHoursText}</dd>
          </div>
        </dl>

        {deleteLocked && court.blockNotice ? <p className="text-xs text-danger-500">{court.blockNotice}</p> : null}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Link
            to={`/venues/${venueId}/courts/${court.courtId}/pricing`}
            className="inline-flex h-8 items-center justify-center rounded-lg px-3 text-xs font-medium text-slateish-600 transition-colors hover:bg-slateish-200"
          >
            요금 설정
          </Link>
          <Link
            to={`/venues/${venueId}/courts/${court.courtId}/operating-hours`}
            className="inline-flex h-8 items-center justify-center rounded-lg px-3 text-xs font-medium text-slateish-600 transition-colors hover:bg-slateish-200"
          >
            운영시간 설정
          </Link>
          <Button variant="secondary" size="sm" disabled={!editable} onClick={onEdit}>
            수정
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="border-danger-500 text-danger-500 hover:bg-danger-100"
            disabled={!editable || deleteLocked}
            onClick={onDelete}
          >
            삭제
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
